/**
 * Property / apartment detail · global search (portfolio-wide).
 *
 * One search box under the tab bar that queries every entity the operator can
 * access — apartments, rooms, tenants (past, current and future) and lessors — across
 * ALL properties, not just the one being viewed. Matches are grouped by entity
 * type; each row is clickable and routes straight to its destination, carrying
 * its own property in the route:
 *
 *   Apartment → apartment detail (Rooms tab)
 *   Room      → parent apartment detail, that room highlighted & scrolled into
 *               view (falls back to the standalone room detail for rooms that
 *               aren't part of an apartment)
 *   Tenant    → tenant profile page
 *   Lessor    → lessors list with that lessor's edit sheet opened (?edit=id)
 *
 * Data comes from useGlobalSearchIndex, which scopes the portfolio to what the
 * operator may see (owner-portal users only get their own properties; tenant
 * matches are super-admin-only). Matching is client-side over that index.
 * Behaviour rules from the spec: query fires at 2+ characters, debounced
 * 200 ms; each group shows its top 5 rows (header carries the true total);
 * within a group, matches in the property you're currently viewing rank first,
 * then exact-prefix → contains, ties broken by name A→Z. Every result carries a
 * disambiguating meta line led by its property so identically-named rooms /
 * apartments across the portfolio stay distinguishable.
 */
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  BedDouble,
  Briefcase,
  Building2,
  Home,
  Search,
  SearchX,
  User,
  X,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { badgeVariants } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { formatEUR } from "@/lib/v2-format";
import { useGlobalSearchIndex } from "@/hooks/v2/useGlobalSearchIndex";
import type { TenantTimeline } from "@/lib/tenant-timeline";
import { useLanguage } from "@/contexts/LanguageContext";

type Props = {
  /** The property being viewed — its matches rank first within each group. */
  currentPropertyId?: number | null;
  /**
   * Adds a "Properties" group (matching name/address/city, routing to the
   * property's own detail page). Off by default — the property-detail page
   * that normally hosts this search has no use for a group that would only
   * ever match the property already being viewed.
   */
  showPropertyGroup?: boolean;
  /**
   * Controlled input value + trailing-edge change handler, so a caller that
   * already holds debounced search state (e.g. the properties list, which
   * also uses the query to filter its grid) can drive this component instead
   * of it owning its own. Mirrors DebouncedSearchInput's value/
   * onDebouncedChange contract. Uncontrolled (internal state only) when
   * omitted.
   */
  value?: string;
  onDebouncedChange?: (value: string) => void;
  placeholder?: string;
  inputId?: string;
  /** Optional element rendered right of the input — e.g. "5 results". */
  trailing?: React.ReactNode;
};

type ResultKind = "apartment" | "room" | "tenant" | "lessor" | "property";

type SearchResult = {
  kind: ResultKind;
  id: string;
  to: string;
  /** Primary label, highlighted against the query. */
  title: string;
  /** Secondary disambiguating line (property, parent, price, status, email…). */
  meta: string;
  /** Right-hand route action label shown on hover / keyboard focus. */
  action: string;
  /** Owning property — drives "current property first" ranking. */
  propertyId?: number | null;
  /** Tenant rows only: past / current / future tag shown beside the title. */
  timeline?: TenantTimeline;
};

// Within the Tenants group, current tenants outrank future, future outrank past.
const TIMELINE_RANK: Record<TenantTimeline, number> = {
  current: 0,
  future: 1,
  past: 2,
};

// Tag styling per timeline, built on the shared Badge variants.
const TIMELINE_BADGE: Record<
  TenantTimeline,
  "default" | "secondary" | "outline"
> = {
  current: "default",
  future: "secondary",
  past: "outline",
};

type ResultGroup = {
  kind: ResultKind;
  label: string;
  icon: typeof Building2;
  total: number;
  items: SearchResult[];
};

// Show at most this many rows per group before the header count carries the total.
const MAX_PER_GROUP = 5;
// Minimum characters before the panel queries anything (spec: 2+).
const MIN_QUERY_LENGTH = 2;
// Trailing-edge debounce for the query that drives results (spec: 200 ms).
const DEBOUNCE_MS = 200;

/** Rank within a group: matches in the current property first, then (tenants)
 *  current → future → past, then exact-prefix, then substring, ties broken by
 *  title A→Z. */
// Exported for the ranking unit test.
// eslint-disable-next-line react-refresh/only-export-components
export function rankResults(
  items: SearchResult[],
  q: string,
  currentPropertyId?: number | null
): SearchResult[] {
  return [...items].sort((a, b) => {
    const aHere = a.propertyId != null && a.propertyId === currentPropertyId;
    const bHere = b.propertyId != null && b.propertyId === currentPropertyId;
    if (aHere !== bHere) return aHere ? -1 : 1;
    const aTime = a.timeline ? TIMELINE_RANK[a.timeline] : 0;
    const bTime = b.timeline ? TIMELINE_RANK[b.timeline] : 0;
    if (aTime !== bTime) return aTime - bTime;
    const aPrefix = a.title.toLowerCase().startsWith(q) ? 0 : 1;
    const bPrefix = b.title.toLowerCase().startsWith(q) ? 0 : 1;
    if (aPrefix !== bPrefix) return aPrefix - bPrefix;
    return a.title.localeCompare(b.title);
  });
}

/** Split `text` on every case-insensitive occurrence of `query`, emphasising
 *  the matched substrings so it's obvious *why* a row surfaced. */
function Highlight({ text, query }: { text: string; query: string }) {
  const q = query.trim();
  if (!q) return <>{text}</>;
  const lower = text.toLowerCase();
  const ql = q.toLowerCase();
  const nodes: React.ReactNode[] = [];
  let i = 0;
  let key = 0;
  while (i < text.length) {
    const found = lower.indexOf(ql, i);
    if (found === -1) {
      nodes.push(text.slice(i));
      break;
    }
    if (found > i) nodes.push(text.slice(i, found));
    nodes.push(
      <mark
        key={key++}
        className="rounded bg-primary/15 px-0.5 font-semibold text-foreground"
      >
        {text.slice(found, found + q.length)}
      </mark>
    );
    i = found + q.length;
  }
  return <>{nodes}</>;
}

export function PropertyGlobalSearch({
  currentPropertyId,
  showPropertyGroup = false,
  value,
  onDebouncedChange,
  placeholder,
  inputId,
  trailing,
}: Props) {
  const { t } = useLanguage();
  const timelineLabel = (timeline: TenantTimeline) =>
    timeline === "past"
      ? t("admin.search.tenantPast", "Past")
      : timeline === "future"
        ? t("admin.search.tenantFuture", "Future")
        : t("admin.search.tenantCurrent", "Current");
  const navigate = useNavigate();

  // Defer the portfolio-wide fetch until the operator engages the search, so a
  // page load doesn't eagerly pull every room/apartment/booking when search is
  // never used. Flips true on first focus (or keystroke) and stays true.
  const [active, setActive] = useState(false);
  const index = useGlobalSearchIndex(active, true);

  // Stable ids for combobox ↔ listbox ↔ active-option wiring (screen readers).
  const listboxId = useId();
  const optionId = (id: string) => `${listboxId}-opt-${id}`;

  const [query, setQuery] = useState("");
  // Debounced, resolved query that actually drives the result set. Kept
  // separate from `query` so the input stays responsive while results settle
  // on the trailing edge — and so previous results stay on screen (no flicker)
  // until the new set resolves.
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const activeRowRef = useRef<HTMLButtonElement>(null);

  // Keep the input in sync when a controlling parent resets `value`
  // externally (e.g. "Clear filters").
  useEffect(() => {
    if (value !== undefined) setQuery(value);
  }, [value]);

  // Trigger + debounce: the suggestion panel fires at 2+ chars on the
  // trailing edge; a shorter query clears it so the panel shows nothing. The
  // caller's grid filter (onDebouncedChange) always gets the raw trailing-edge
  // value regardless of length, so a 1-character term still filters the grid
  // even though the dropdown itself stays gated at MIN_QUERY_LENGTH — and so
  // it isn't trimmed out from under a user who paused mid-word with a
  // trailing space.
  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < MIN_QUERY_LENGTH) {
      setDebouncedQuery("");
    }
    const timer = setTimeout(() => {
      if (trimmed.length >= MIN_QUERY_LENGTH) setDebouncedQuery(trimmed);
      onDebouncedChange?.(query);
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const {
    properties,
    apartments,
    rooms,
    occupants,
    tenants,
    lessors,
    propertyLessorId,
  } = index;

  const lessorById = useMemo(() => {
    const m = new Map<string, (typeof lessors)[number]>();
    for (const l of lessors) m.set(l.id, l);
    return m;
  }, [lessors]);

  const apartmentById = useMemo(() => {
    const m = new Map<number, (typeof apartments)[number]>();
    for (const a of apartments) m.set(a.id, a);
    return m;
  }, [apartments]);

  const roomById = useMemo(() => {
    const m = new Map<number, (typeof rooms)[number]>();
    for (const r of rooms) m.set(r.id, r);
    return m;
  }, [rooms]);

  const roomCountByApartment = useMemo(() => {
    const m = new Map<number, number>();
    for (const r of rooms) {
      if (r.apartment_id == null) continue;
      m.set(r.apartment_id, (m.get(r.apartment_id) ?? 0) + 1);
    }
    return m;
  }, [rooms]);

  // ── Build grouped results ───────────────────────────────────────────────
  const { groups, flat } = useMemo(() => {
    const q = debouncedQuery.trim().toLowerCase();
    if (q.length < MIN_QUERY_LENGTH)
      return { groups: [] as ResultGroup[], flat: [] as SearchResult[] };
    const inc = (s?: string | null) => !!s && s.toLowerCase().includes(q);

    const propTitle = (id: number) => properties.get(id)?.title ?? "";
    const propAddr = (id: number) => properties.get(id)?.address ?? "";
    const lessorName = (id: string | null) =>
      id ? (lessorById.get(id)?.name ?? "") : "";

    // Apartments — label, property title/street, floor, owner (lessor) name.
    const apartmentResults: SearchResult[] = apartments
      .filter(
        (a) =>
          inc(a.label) ||
          inc(a.floor) ||
          inc(propTitle(a.property_id)) ||
          inc(propAddr(a.property_id)) ||
          inc(lessorName(a.lessor_id))
      )
      .map((a) => {
        const owner = lessorName(a.lessor_id);
        const count = roomCountByApartment.get(a.id) ?? 0;
        const meta = [
          propTitle(a.property_id) || null,
          a.floor ? `Floor ${a.floor}` : null,
          `${count} ${count === 1 ? "room" : "rooms"}`,
          owner || null,
        ]
          .filter(Boolean)
          .join(" · ");
        return {
          kind: "apartment" as const,
          id: `apt-${a.id}`,
          to: `/admin/properties/${a.property_id}/apartments/${a.id}`,
          title: a.label,
          meta,
          action: t("admin.apartment.open", "Open"),
          propertyId: a.property_id,
        };
      });

    // Rooms — name/number (in title), floor, size, parent apartment, property
    // title/street, and (super admins) active occupant.
    const roomResults: SearchResult[] = rooms
      .filter((r) => !r.is_hidden)
      .filter((r) => {
        const occ = occupants.get(r.id);
        const apt =
          r.apartment_id != null ? apartmentById.get(r.apartment_id) : null;
        return (
          inc(r.title) ||
          inc(r.floor) ||
          (r.size_sqm != null && String(r.size_sqm).includes(q)) ||
          inc(apt?.label) ||
          inc(propTitle(r.property_id)) ||
          inc(propAddr(r.property_id)) ||
          inc(occ?.name) ||
          inc(occ?.email)
        );
      })
      .map((r) => {
        const occupied = occupants.has(r.id);
        const status = occupied
          ? t("admin.field.occupied", "Occupied")
          : r.status === "maintenance"
            ? t("admin.status.maintenance", "Maintenance")
            : t("admin.status.vacant", "Vacant");
        const apt =
          r.apartment_id != null ? apartmentById.get(r.apartment_id) : null;
        const meta = [
          propTitle(r.property_id) || null,
          apt ? apt.label : null,
          formatEUR(r.price) ? `${formatEUR(r.price)} / month` : null,
          status,
        ]
          .filter(Boolean)
          .join(" · ");
        // Spec: a room opens its PARENT apartment with the room highlighted.
        // Rooms not attached to an apartment fall back to the room detail page.
        const to = apt
          ? `/admin/properties/${r.property_id}/apartments/${apt.id}?room=${r.id}`
          : `/admin/properties/${r.property_id}/rooms/${r.id}`;
        return {
          kind: "room" as const,
          id: `room-${r.id}`,
          to,
          title: r.title,
          meta,
          action: t("admin.apartment.open", "Open"),
          propertyId: r.property_id,
        };
      });

    // Tenants — full name, email, phone, assigned room / apartment, across
    // past, current and future bookings (super admins only).
    const tenantResults: SearchResult[] = tenants
      .filter((o) => {
        const room = roomById.get(o.roomId);
        const apt =
          room?.apartment_id != null
            ? apartmentById.get(room.apartment_id)
            : null;
        return (
          inc(o.name) ||
          inc(o.email) ||
          inc(o.phone) ||
          inc(room?.title) ||
          inc(apt?.label)
        );
      })
      .map((o) => {
        const room = roomById.get(o.roomId);
        const apt =
          room?.apartment_id != null
            ? apartmentById.get(room.apartment_id)
            : null;
        const propertyId = room?.property_id ?? null;
        const meta = [
          propertyId != null ? propTitle(propertyId) : null,
          room?.title ?? null,
          apt?.label ?? null,
          o.email ?? null,
        ]
          .filter(Boolean)
          .join(" · ");
        // A tenant's booking is only routable when we can resolve its room's
        // property (always true for indexed rooms); guard defensively.
        const to =
          propertyId != null
            ? `/admin/properties/${propertyId}/rooms/${o.roomId}/tenants/${o.bookingId}`
            : "";
        return {
          kind: "tenant" as const,
          id: `tenant-${o.bookingId}`,
          to,
          title: o.name?.trim() || o.email?.trim() || `Tenant #${o.bookingId}`,
          meta,
          action: t("admin.tenant.openProfile", "Open profile"),
          propertyId,
          timeline: o.timeline,
        };
      })
      .filter((r) => r.to !== "");

    // Properties — name, address, city. Only built when the caller opts in;
    // the property-detail page's search has no use for a group that could
    // only ever match the one property already being viewed.
    const propertyResults: SearchResult[] = showPropertyGroup
      ? Array.from(properties.values())
          .filter((p) => inc(p.title) || inc(p.address) || inc(p.city))
          .map((p) => ({
            kind: "property" as const,
            id: `property-${p.id}`,
            to: `/admin/properties/${p.id}`,
            title: p.title,
            meta: [p.address, p.city].filter(Boolean).join(", "),
            action: t("admin.apartment.open", "Open"),
            propertyId: p.id,
          }))
      : [];

    // Lessors — every lessor referenced by an accessible property or apartment.
    const lessorIds = new Set<string>();
    for (const id of propertyLessorId.values()) if (id) lessorIds.add(id);
    for (const a of apartments) if (a.lessor_id) lessorIds.add(a.lessor_id);
    const lessorResults: SearchResult[] = Array.from(lessorIds)
      .map((id) => lessorById.get(id))
      .filter((l): l is (typeof lessors)[number] => !!l)
      .filter((l) => inc(l.name) || inc(l.rep_name) || inc(l.email))
      .map((l) => {
        const meta = [l.type ?? null, l.rep_name ?? null, l.email ?? null]
          .filter(Boolean)
          .join(" · ");
        return {
          kind: "lessor" as const,
          id: `lessor-${l.id}`,
          to: `/admin/owners?edit=${l.id}`,
          title: l.name,
          meta,
          action: t("admin.action.edit", "Edit"),
        };
      });

    const built: ResultGroup[] = [];
    if (propertyResults.length)
      built.push({
        kind: "property",
        label: t("admin.nav.properties", "Properties"),
        icon: Home,
        total: propertyResults.length,
        items: rankResults(propertyResults, q, currentPropertyId).slice(
          0,
          MAX_PER_GROUP
        ),
      });
    if (apartmentResults.length)
      built.push({
        kind: "apartment",
        label: t("admin.field.apartments", "Apartments"),
        icon: Building2,
        total: apartmentResults.length,
        items: rankResults(apartmentResults, q, currentPropertyId).slice(
          0,
          MAX_PER_GROUP
        ),
      });
    if (roomResults.length)
      built.push({
        kind: "room",
        label: t("admin.section.rooms", "Rooms"),
        icon: BedDouble,
        total: roomResults.length,
        items: rankResults(roomResults, q, currentPropertyId).slice(
          0,
          MAX_PER_GROUP
        ),
      });
    if (tenantResults.length)
      built.push({
        kind: "tenant",
        label: t("admin.nav.tenants", "Tenants"),
        icon: User,
        total: tenantResults.length,
        items: rankResults(tenantResults, q, currentPropertyId).slice(
          0,
          MAX_PER_GROUP
        ),
      });
    if (lessorResults.length)
      built.push({
        kind: "lessor",
        label: t("admin.nav.lessors", "Lessors"),
        icon: Briefcase,
        total: lessorResults.length,
        items: rankResults(lessorResults, q, currentPropertyId).slice(
          0,
          MAX_PER_GROUP
        ),
      });

    const flatItems = built.flatMap((g) => g.items);
    return { groups: built, flat: flatItems };
  }, [
    debouncedQuery,
    properties,
    apartments,
    rooms,
    occupants,
    tenants,
    lessorById,
    apartmentById,
    roomById,
    roomCountByApartment,
    propertyLessorId,
    currentPropertyId,
    showPropertyGroup,
    t,
  ]);

  const hasQuery = debouncedQuery.length >= MIN_QUERY_LENGTH;
  const showPanel = open && hasQuery;
  const totalResults = groups.reduce((n, g) => n + g.total, 0);
  // Skeleton while the portfolio index is still resolving and we don't yet have
  // anything to show — keeps the panel from flashing "no matches" prematurely.
  const showSkeleton = totalResults === 0 && index.isLoading;

  // Reset the keyboard cursor whenever the resolved result set changes.
  useEffect(() => {
    setActiveIndex(0);
  }, [debouncedQuery]);

  // Keep the active row scrolled into view during keyboard navigation.
  useEffect(() => {
    activeRowRef.current?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  // Close on outside click.
  useEffect(() => {
    if (!showPanel) return;
    function onPointerDown(e: PointerEvent) {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [showPanel]);

  function go(result: SearchResult) {
    setOpen(false);
    navigate(result.to);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Escape") {
      setOpen(false);
      return;
    }
    if (!showPanel || flat.length === 0) {
      if (e.key === "ArrowDown" && hasQuery) setOpen(true);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, flat.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const target = flat[activeIndex];
      if (target) go(target);
    }
  }

  // Precompute each group's starting offset into `flat` so a row can tell
  // whether it's the keyboard-active one.
  let runningIndex = 0;

  return (
    <div ref={wrapperRef} className="relative">
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            type="search"
            value={query}
            onChange={(e) => {
              setActive(true);
              setQuery(e.target.value);
              setOpen(true);
            }}
            onFocus={() => {
              setActive(true);
              if (hasQuery) setOpen(true);
            }}
            onKeyDown={onKeyDown}
            placeholder={
              placeholder ??
              t(
                "admin.search.globalPlaceholder",
                "Search apartments, rooms, tenants or lessors…"
              )
            }
            id={inputId}
            className="pl-9 pr-9"
            role="combobox"
            aria-expanded={showPanel}
            aria-autocomplete="list"
            aria-controls={showPanel ? listboxId : undefined}
            aria-activedescendant={
              showPanel && flat[activeIndex]
                ? optionId(flat[activeIndex].id)
                : undefined
            }
          />
          {query.length > 0 && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2"
              onClick={() => {
                setQuery("");
                setDebouncedQuery("");
                onDebouncedChange?.("");
                setOpen(false);
              }}
              aria-label={t("admin.aria.clearSearch", "Clear search")}
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
        {trailing && (
          <div className="text-xs text-muted-foreground whitespace-nowrap bg-muted/40 border border-border/60 rounded-full px-2.5 py-1">
            {trailing}
          </div>
        )}
      </div>

      {showPanel && (
        <Card className="absolute left-0 right-0 top-full z-50 mt-2 overflow-hidden p-0 shadow-lg">
          {showSkeleton ? (
            <div className="space-y-2 p-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3">
                  <Skeleton className="h-9 w-9 rounded-md" />
                  <div className="flex-1 space-y-1.5">
                    <Skeleton className="h-3.5 w-40" />
                    <Skeleton className="h-3 w-56" />
                  </div>
                </div>
              ))}
            </div>
          ) : totalResults === 0 ? (
            <div className="flex flex-col items-center gap-2 px-6 py-12 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                <SearchX className="h-5 w-5 text-muted-foreground" />
              </div>
              <p className="font-semibold">
                {t("admin.search.noMatches", "No matches for")} “
                {debouncedQuery}”
              </p>
              <p className="text-sm text-muted-foreground">
                {t(
                  "admin.search.noMatchesHint",
                  "Try a room name, tenant, apartment or owner."
                )}
              </p>
            </div>
          ) : (
            <>
              <div
                id={listboxId}
                role="listbox"
                aria-label={t("admin.search.resultsLabel", "Search results")}
                className="max-h-[24rem] overflow-y-auto py-1"
              >
                {groups.map((group) => {
                  const Icon = group.icon;
                  return (
                    <div key={group.kind} role="group" aria-label={group.label}>
                      <div
                        aria-hidden="true"
                        className="flex items-center justify-between px-3 pb-1 pt-2"
                      >
                        <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                          <Icon className="h-3.5 w-3.5" />
                          {group.label}
                          <span className="tabular-nums opacity-70">
                            {group.total}
                          </span>
                        </div>
                      </div>
                      {group.items.map((result) => {
                        const idx = runningIndex++;
                        const isActive = idx === activeIndex;
                        return (
                          <button
                            key={result.id}
                            type="button"
                            id={optionId(result.id)}
                            role="option"
                            aria-selected={isActive}
                            ref={isActive ? activeRowRef : undefined}
                            onMouseEnter={() => setActiveIndex(idx)}
                            onClick={() => go(result)}
                            className={`flex w-full items-center gap-3 border-l-[3px] px-3 py-2 text-left transition-colors ${
                              isActive
                                ? "border-primary bg-primary/[0.09]"
                                : "border-transparent hover:bg-muted/60"
                            }`}
                          >
                            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-muted">
                              <Icon className="h-4 w-4 text-muted-foreground" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex min-w-0 items-center gap-2">
                                <span className="truncate text-sm font-medium">
                                  <Highlight
                                    text={result.title}
                                    query={debouncedQuery}
                                  />
                                </span>
                                {result.timeline && (
                                  <span
                                    className={cn(
                                      badgeVariants({
                                        variant:
                                          TIMELINE_BADGE[result.timeline],
                                      }),
                                      "shrink-0 px-1.5 py-0 text-[10px] font-medium leading-4"
                                    )}
                                  >
                                    {timelineLabel(result.timeline)}
                                  </span>
                                )}
                              </div>
                              {result.meta && (
                                <div className="truncate text-xs text-muted-foreground">
                                  <Highlight
                                    text={result.meta}
                                    query={debouncedQuery}
                                  />
                                </div>
                              )}
                            </div>
                            <span
                              className={`hidden shrink-0 items-center gap-1 text-xs font-medium text-primary sm:flex ${
                                isActive ? "opacity-100" : "opacity-0"
                              }`}
                            >
                              {result.action}
                              <ArrowRight className="h-3.5 w-3.5" />
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center justify-between border-t bg-muted/30 px-3 py-1.5 text-[11px] text-muted-foreground">
                <span className="hidden gap-2 sm:flex">
                  <span>↑↓ {t("admin.search.navigate", "Navigate")}</span>
                  <span>↵ {t("admin.apartment.open", "Open")}</span>
                  <span>esc {t("admin.action.close", "Close")}</span>
                </span>
                <span className="tabular-nums">
                  {totalResults}{" "}
                  {totalResults === 1
                    ? t("admin.search.result", "result")
                    : t("admin.search.results", "results")}
                </span>
              </div>
            </>
          )}
        </Card>
      )}
    </div>
  );
}
